import React, { useRef, useState } from 'react'
import { connect } from 'react-redux'
import DisplaysToolbarContent from '../__toolbarContent/displays__toolbarContent'
import Sidebar from 'blocks.app/sidebar/sidebar'
import TagsList from 'blocks.app/tagsList/tagsList'
import { api } from 'core/api/ConnectionManager'
import helpers from 'core/helpers'
import helpersDigitalSignages from 'core/helpers/_digitalSignages'
import translate from 'core/translate'
import { isNotEmptyArray } from 'core/utils/coreUtil'
import { displaysActions } from '../displays.state'
import { IDevice } from '../../../core/models/DigitalSignage'
import { Legend } from './legend/Legend'
import { DisplaysTopContent } from './DisplaysTopContent/DisplaysTopContent'

interface IData {
    sourceId: number
    groupId: number | null
    folderId: number
    overwriteSettings: any
    groupScheduleMode: string
    newFolderId: number
}

type SelectedItem = {
    identificationImplementedQ?: boolean
    online?: boolean
    type: string
    id: number
    status: string
}

interface IDisplaysToolbarProps {
    onChange: (data: any) => void
    selectedInfo: any
    selectedItems: SelectedItem[]
    onCloseInfo: () => void
    openModal: (data: { modalState: string; modalOptions: { devices: any; groups: any } }) => void
    tags: any
    withoutAddress: string[]
    onGetFoldersOnMultiple: () => void
    downloadLink: string
    groupId: number | null
    groups: any
    onSelectInfo: any
    query: any
}

const getDevicesFromSelected = (selected: any) =>
    selected.filter((selectedItem: any) => !helpersDigitalSignages.isFolder(selectedItem))
const getGroupsFromSelected = (selected: any) =>
    selected.filter((selectedItem: any) => helpersDigitalSignages.isFolder(selectedItem))

const DisplaysToolbar = (p_: IDisplaysToolbarProps) => {
    const [showWarningOnMultipleChangeSettings, setShowWarningOnMultipleChangeSettings] = useState<boolean>(false)
    const [showDisplayStatuses, setShowDisplayStatuses] = useState<boolean>(false)
    const moveItemRef = useRef<any>(null)

    const isAvailableIdentification = () => {
        if (p_.selectedItems && p_.selectedItems.length) {
            return p_.selectedItems.some((item) => item.identificationImplementedQ)
        }

        return false
    }

    const onCloseWarning = () => {
        setShowWarningOnMultipleChangeSettings(false)
    }
    const onAcceptWarning = () => {
        const { openModal } = p_
        const devices = getDevicesFromSelected(p_.selectedItems)
        const groups = getGroupsFromSelected(p_.selectedItems)

        onCloseWarning()

        openModal({
            modalState: 'displaySettings',
            modalOptions: {
                devices,
                groups,
            },
        })
    }

    const updateDsTag = (data: { id: number; name: string }) => api.send('updateDsTag', data)
    const deleteDsTag = (data: { id: number; name: string }) => api.send('deleteDsTag', data)
    const createDsTag = (data: { id: number; name: string }) => api.send('createDsTag', data)

    const getCustomContent = () => {
        if (p_.selectedInfo || p_.withoutAddress.length) {
            // @ts-ignore
            return <DisplaysToolbarContent {...p_} />
        }

        if (p_.selectedItems.length) {
            return null
        }

        return (
            <TagsList
                title={translate('tags')}
                className={'test-device-tag'}
                tags={p_.tags}
                onUpdate={updateDsTag}
                onDelete={deleteDsTag}
                onCreate={createDsTag}
                onlyRead={
                    !helpers.isAvailableAction(null, {
                        key: 'digitalSignage',
                        action: 'update',
                        subaction: 'manageTags',
                    })
                }
            />
        )
    }

    const isShowAdditionContent = (selectedItems: SelectedItem[]) => {
        let isShowAdditionContent = false

        if (!selectedItems || !isNotEmptyArray(selectedItems)) return isShowAdditionContent

        selectedItems.forEach((item) => {
            if (item.type === 'digitalSignage') {
                isShowAdditionContent = true
            }
        })

        return isShowAdditionContent
    }

    const deleteDisplayGroups = () => {
        let idDisplays: number[] = []
        let idGroups: number[] = []

        p_.selectedItems.forEach((item) => {
            if (item.type === 'group') {
                idGroups.push(item.id)
            } else {
                idDisplays.push(item.id)
            }
        })

        Promise.all([api.send('deleteGroup', { id: idGroups }), api.send('deleteDisplay', { id: idDisplays })]).then(
            () => {
                p_.onChange([])
            }
        )
    }

    const editDisplayGroups = (data: IData) => {
        Promise.all([
            api.send('editDisplay', {
                id: data.sourceId,
                groupId: data.newFolderId,
                overwriteSettings: data.overwriteSettings,
                groupScheduleMode: data.groupScheduleMode,
            }),
            api.send('editGroup', {
                id: data.folderId,
                parentId: data.newFolderId,
            }),
        ]).then(() => {
            p_.onChange([])
        })
    }

    const getSelectInfoStatus = (selectedInfo: SelectedItem) => {
        return selectedInfo ? selectedInfo.status : null
    }

    const isErrorStatus = (selectedInfo: SelectedItem) => {
        return getSelectInfoStatus(selectedInfo) === 'error'
    }

    const onlineSelectedItems = p_.selectedItems.filter(
        (selectedItem) => selectedItem.status === 'online' || selectedItem.status === 'online_display_off'
    )
    const devices: IDevice[] = getDevicesFromSelected(p_.selectedItems)
    const groups: IDevice[] = getGroupsFromSelected(p_.selectedItems)
    const isErrorCode = p_.selectedInfo && p_.selectedInfo.errorCode
    const _isErrorStatus = isErrorStatus(p_.selectedInfo)
    const isEditable =
        !helpers.isSomeNotHaveAction(p_.selectedItems, 'update') &&
        helpers.isAvailableAction(null, {
            key: 'digitalSignage',
            action: 'update',
            subaction: 'generalEditing',
        })

    return (
        <Sidebar
            selectedSide="right"
            onSelectInfo={p_.onSelectInfo}
            onChange={(list: {}[] | []) => p_.onChange(list)}
            onGetFoldersOnMultiple={p_.onGetFoldersOnMultiple}
            icon={{
                right: {
                    name: isErrorCode && _isErrorStatus ? 'attention' : null,
                    color: isErrorCode && _isErrorStatus ? 'red' : null,
                },
            }}
            customHeader={
                <Legend showDisplayStatuses={showDisplayStatuses} setShowDisplayStatuses={setShowDisplayStatuses} />
            }
            topContent={{
                right: (
                    <DisplaysTopContent
                        onlineSelectedItems={onlineSelectedItems}
                        isAvailableIdentification={isAvailableIdentification}
                        devices={devices}
                        groups={groups}
                        p_={p_}
                        showWarningOnMultipleChangeSettings={showWarningOnMultipleChangeSettings}
                        onCloseWarning={onCloseWarning}
                        onAcceptWarning={onAcceptWarning}
                        setShowWarningOnMultipleChangeSettings={setShowWarningOnMultipleChangeSettings}
                        isEditable={isEditable}
                        moveItemRef={moveItemRef}
                        onGetFoldersOnMultiple={p_.onGetFoldersOnMultiple}
                    />
                ),
            }}
            customContent={{
                right: getCustomContent(),
            }}
            content={{
                right:
                    !p_.selectedInfo && p_.selectedItems.length
                        ? {
                              title: `${translate('countOfSelectedDevices')}: ${
                                  p_.selectedItems ? p_.selectedItems.length : ''
                              }`,
                              options: p_.selectedItems,
                              folders: p_.groups,
                              editTitle: translate('parentFolder'),
                              folderId: p_.groupId,
                              isDirectory: (option: { type: string }) => option.type === 'group',
                              additionContent: isShowAdditionContent(p_.selectedItems)
                                  ? [
                                        {
                                            title: translate('applyGroupSettings'),
                                            name: 'overwriteSettings',
                                            type: 'checkbox',
                                            initValue: false,
                                        },
                                        {
                                            title: translate('scheduleForDevice'),
                                            name: 'groupScheduleMode',
                                            type: 'radio',
                                            options: [
                                                {
                                                    label: translate('changeCurrentSchedule'),
                                                    value: 'apply',
                                                },
                                                {
                                                    label: translate('remainCurrentSchedule'),
                                                    value: 'ignore',
                                                },
                                            ],
                                            initValue: 'ignore',
                                        },
                                    ]
                                  : null,
                          }
                        : null,
            }}
            isEmpty={{
                right: false,
            }}
            isEditable={isEditable}
            onSubmit={(side: string, type: string, data: IData) => {
                if (side === 'right' && type === 'delete') {
                    deleteDisplayGroups()
                }

                if (side === 'right' && type === 'edit') {
                    editDisplayGroups(data)
                }
            }}
            moveItemRef={moveItemRef}
        />
    )
}

const mapStateToProps = (state: any) => ({
    tags: state.displays.tags,
    withoutAddress: state.displays.withoutAddress || [],
    toolbar: state.displays.toolbar,
    broadcasts: state.displays.broadcasts || [],
})

const mapDispatchToProps = (dispatch: any) => {
    return {
        openModal: (modalOptions: any) => dispatch(displaysActions.openModal(modalOptions)),
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(DisplaysToolbar)
